import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "col-main" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "col-unit-right"
}
const _hoisted_4 = { class: "input-unit--right" }
const _hoisted_5 = { class: "input-icon--right" }
const _hoisted_6 = {
  key: 2,
  class: "col-check"
}
const _hoisted_7 = { class: "icon" }
const _hoisted_8 = { class: "icon" }
const _hoisted_9 = ["innerHTML"]

import Tooltip from '@/components/Tooltip.vue';
import {
  computed,
  ref,
  onMounted,
  watch,
} from 'vue';
import TextField from '@/components/inputs/TextField.vue';
import TextareaField from '@/components/inputs/TextareaField.vue';
import NumberField from '@/components/inputs/NumberField.vue';
import DateField from '@/components/inputs/DateField.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner,
  faCheck,
  faExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faCalendarDays,
} from '@fortawesome/pro-regular-svg-icons';

interface GridItems {
  main: string
  unit?: string,
  iconRight?: string
  check?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'InputField',
  props: /*#__PURE__*/_mergeModels({
    type: { default: 'text' },
    name: {},
    label: {},
    unit: {},
    readonly: { type: Boolean },
    disabled: { type: Boolean },
    minDate: {},
    maxDate: {},
    minLength: {},
    maxLength: {},
    min: {},
    max: {},
    onlyNumeric: { type: Boolean },
    errorNumeric: {},
    showLoader: { type: Boolean },
    isValid: { type: Boolean },
    validColor: {},
    showValidCheck: { type: Boolean },
    disableWeekend: { type: Boolean },
    hideLabel: { type: Boolean },
    preventPaste: { type: Boolean }
  }, {
    "modelValue": {},
    "modelModifiers": {},
    "error": {},
    "errorModifiers": {},
  }),
  emits: /*#__PURE__*/_mergeModels(['input'], ["update:modelValue", "update:error"]),
  setup(__props: any, { emit: __emit }) {

library.add(faCalendarDays, faSpinner, faCheck, faExclamation);

const emit = __emit;

const modelValue = _useModel(__props, "modelValue");
const error = _useModel<undefined | string | string[]>(__props, 'error');
const props = __props;

const componentId = `if-${Math.random().toString(36).substring(2, 11)}`;
const componentError = ref<string | string[]>();
watch(() => error.value, () => {
  componentError.value = error.value;
}, {
  immediate: true,
  deep: true,
});
watch(() => modelValue.value, () => {
  error.value = undefined;
}, {
  deep: true,
});

const componentType = computed(() => {
  switch (props.type) {
    case 'date':
      return DateField;
    case 'number':
      return NumberField;
    case 'textarea':
      return TextareaField;
    default:
      return TextField;
  }
});

const input = ref();

const errorMessage = computed<string | undefined>(() => {
  if (Array.isArray(componentError.value)) {
    if (componentError.value[0]) {
      return `${componentError.value[0]}`.trim();
    }
  } else if (componentError.value) {
    return `${componentError.value}`.trim();
  }

  return undefined;
});
const hasError = computed<boolean>(() => errorMessage.value !== undefined);

const onPaste = (e: Event) => {
  if (props.preventPaste) {
    e.preventDefault();
  }
};

const emitInput = ($value: unknown) => {
  emit('input', $value);
};

const onInput = ($value: string | number) => {
  let formattedVal = $value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      formattedVal = formattedVal.replace(/\D/g, '');
    }

    if (props.minLength !== undefined && formattedVal.length < props.minLength) {
      componentError.value = `Mindestens ${props.minLength} Zeichen`;
      if (props.onlyNumeric) {
        componentError.value = props.errorNumeric || `Zeichenfolge aus mindestens ${props.minLength} Zahlen`;
      }
    } else if (props.maxLength !== undefined && formattedVal.length > props.maxLength) {
      formattedVal = formattedVal.substring(0, props.maxLength);
      if (input.value) {
        input.value.$el.value = formattedVal;
      }
    }
    if (props.minLength !== undefined && formattedVal.length >= props.minLength) {
      componentError.value = [];
    }
  }

  modelValue.value = formattedVal;
  emitInput(formattedVal);
};

const onBlur = () => {
  let formattedVal = modelValue.value;

  if (typeof formattedVal === 'string') {
    if (props.onlyNumeric) {
      let stringVal = formattedVal as string;

      stringVal = stringVal.replace(/\D/g, '');

      const numberValue = parseInt(stringVal, 10);

      if (input.value) {
        if (props.min !== undefined && numberValue < props.min) {
          formattedVal = `${props.min}`;
        }

        if (props.max !== undefined && numberValue > props.max) {
          formattedVal = `${props.max}`;
        }

        input.value.$el.value = formattedVal;
      } else if (props.min !== undefined) {
        input.value.$el.value = formattedVal;
      }
    }
  } else if (formattedVal !== undefined && props.type === 'number' && input.value) {
    const numberValue = formattedVal as number;

    if (props.min !== undefined && numberValue < props.min) {
      formattedVal = props.min;
    }

    if (props.max !== undefined && numberValue > props.max) {
      formattedVal = props.max;
    }

    input.value.$el.value = formattedVal;
  }

  modelValue.value = formattedVal;
  emitInput(formattedVal);
};

const focused = ref(false);
const filled = computed(() => `${typeof modelValue.value === 'number' ? modelValue.value : modelValue.value || ''}`.trim() !== '');
const showIconDate = computed(() => props.type === 'date');
const showUnit = computed(() => !showIconDate.value && props.unit);
const showValidCheck = computed(() => props.isValid);
const validCheckColor = computed<string>(() => {
  let colors = Array.isArray(props.validColor) ? props.validColor : [props.validColor];
  colors = colors.filter((color) => !!color).map((color) => `${color}`.trim());

  if (colors.length >= 2) {
    return `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  }

  return colors[0];
});

const showInvalidCross = computed(() => errorMessage.value !== undefined && !showValidCheck.value);

const inputGrid = computed<{
  items: GridItems
  columns: string[]
}>(() => {
  const columns = [];
  const items: GridItems = {
    main: '1 / 2',
  };

  columns.push('1fr');
  items.main = `${columns.length} / ${columns.length + 1}`;

  if (showUnit.value) {
    columns.push('auto');
    items.unit = `${columns.length} / ${columns.length + 1}`;
  }

  if (props.showLoader || showIconDate.value) {
    columns.push('auto');
    items.iconRight = `${columns.length} / ${columns.length + 1}`;
  }

  if (showValidCheck.value || showInvalidCross.value) {
    columns.push('auto');
    items.check = `${columns.length} / ${columns.length + 1}`;
  }

  return {
    items,
    columns,
  };
});

const cssVariables = computed<Record<string, string | undefined>>(() => ({
  '--grid-columns': inputGrid.value.columns.join(' '),
  '--grid-column-main': inputGrid.value.items.main,
  '--grid-column-check': inputGrid.value.items.check,
  '--grid-column-unit': inputGrid.value.items.unit,
  '--grid-column-unit-padding': (inputGrid.value.items.check || inputGrid.value.items.iconRight) ? '12px' : '1.875em',
  '--grid-column-icon-right': inputGrid.value.items.iconRight,
  '--grid-column-icon-right-padding': inputGrid.value.items.check ? '12px' : '1.875em',
}));

onMounted(() => {
  emitInput(modelValue.value);
});

return (_ctx: any,_cache: any) => {
  const _component_FontAwesomeIcon = _resolveComponent("FontAwesomeIcon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-field", {
        'readonly': _ctx.readonly,
        'disabled': _ctx.disabled,
        'valid': !_ctx.showLoader && !hasError.value && showValidCheck.value,
        'loading': _ctx.showLoader,
        'icon-right': showIconDate.value || _ctx.showLoader,
        'filled': filled.value,
        'focused': focused.value,
        'label-hidden': _ctx.hideLabel,
        [_ctx.type]: true,
      }]),
    style: _normalizeStyle(cssVariables.value),
    onClick: _cache[3] || (_cache[3] = ($event: any) => (input.value.$el ? input.value.$el.focus({ preventScroll: true }) : false))
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", null, [
        _createElementVNode("label", {
          for: componentId,
          innerHTML: _ctx.label
        }, null, 8, _hoisted_2),
        (_openBlock(), _createBlock(_resolveDynamicComponent(componentType.value), {
          id: componentId,
          readonly: _ctx.readonly,
          disabled: _ctx.disabled,
          "min-date": _ctx.minDate,
          "max-date": _ctx.maxDate,
          ref_key: "input",
          ref: input,
          "disable-weekend": _ctx.disableWeekend,
          value: modelValue.value,
          onKeydown: _cache[0] || (_cache[0] = ($event: any) => (componentError.value = [])),
          "onUpdate:modelValue": onInput,
          onBlur: onBlur,
          onFocusin: _cache[1] || (_cache[1] = ($event: any) => (focused.value = true)),
          onFocusout: _cache[2] || (_cache[2] = ($event: any) => (focused.value = false)),
          onPaste: onPaste,
          onDrop: onPaste
        }, null, 40, ["readonly", "disabled", "min-date", "max-date", "disable-weekend", "value"]))
      ])
    ]),
    (showUnit.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.unit), 1)
        ]))
      : _createCommentVNode("", true),
    (showIconDate.value || _ctx.showLoader)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["col-icon-right", { 'loader': _ctx.showLoader }])
        }, [
          _createElementVNode("span", _hoisted_5, [
            (showIconDate.value)
              ? (_openBlock(), _createBlock(_component_FontAwesomeIcon, {
                  key: 0,
                  icon: "fa-regular fa-calendar-days"
                }))
              : (_openBlock(), _createBlock(_component_FontAwesomeIcon, {
                  key: 1,
                  class: "fa-pulse",
                  icon: "fa-solid fa-spinner"
                }))
          ])
        ], 2))
      : _createCommentVNode("", true),
    (showValidCheck.value || showInvalidCross.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (showValidCheck.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "valid-check",
                style: _normalizeStyle({
            background: validCheckColor.value,
          })
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_component_FontAwesomeIcon, { icon: "fa-solid fa-check" })
                ])
              ], 4))
            : (_openBlock(), _createBlock(Tooltip, {
                key: 1,
                class: "invalid-check",
                "is-error": ""
              }, {
                title: _withCtx(() => [
                  _createElementVNode("span", { innerHTML: errorMessage.value }, null, 8, _hoisted_9)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_FontAwesomeIcon, { icon: "fa-solid fa-exclamation" })
                  ])
                ]),
                _: 1
              }))
        ]))
      : _createCommentVNode("", true)
  ], 6))
}
}

})